import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { key } from '../store'
import DigitalKeysRepository from '@/api/repositories/digitalKeys'
import { UAParser } from 'ua-parser-js'
import saveState from '@/utils/save-state'
import getSavedState from '@/utils/get-saved-state'
import { config } from '@/constants'

export interface AppleWalletJws {
  header: {
    kid: string
  }
  payload: string
  protected: string
  signature: string
}

export const DIGITAL_KEY_STORAGE_KEY = 'digitalKey'

export interface DigitalKeyPersistedState {
  encryptedBookingNo?: string
  encryptedCheckinNo?: string
  email: string
  phone: string
  verificationMethod: 'sms' | 'email'
  preferredName: string
  propertyName: string
  bookingNo: string
}

export const useDigitalKeyStore = defineStore('digitalKeys', () => {
  const persisted = getSavedState<DigitalKeyPersistedState>(
    DIGITAL_KEY_STORAGE_KEY,
    window.sessionStorage,
  )

  const encryptedBookingNo = ref<string | undefined>(persisted?.encryptedBookingNo)
  const encryptedCheckinNo = ref<string | undefined>(persisted?.encryptedCheckinNo)
  const email = ref<string>(persisted?.email ?? '')
  const phone = ref<string>(persisted?.phone ?? '')
  const verificationMethod = ref<'sms' | 'email'>(persisted?.verificationMethod ?? 'email')
  const store = useStore(key)
  const preferredName = ref<string>(persisted?.preferredName ?? '')
  const propertyName = ref<string>(persisted?.propertyName ?? 'Section L')
  const bookingNo = ref<string>(persisted?.bookingNo ?? '')
  const appleWalletJws = ref<AppleWalletJws | undefined>(undefined)
  const digitalKeyCardImageUrl = ref<string | null>(null)
  const { t } = useI18n()
  const router = useRouter()

  watch(
    [
      encryptedBookingNo,
      encryptedCheckinNo,
      email,
      phone,
      verificationMethod,
      preferredName,
      propertyName,
      bookingNo,
    ],
    () => {
      saveState(
        DIGITAL_KEY_STORAGE_KEY,
        {
          encryptedBookingNo: encryptedBookingNo.value,
          encryptedCheckinNo: encryptedCheckinNo.value,
          email: email.value,
          phone: phone.value,
          verificationMethod: verificationMethod.value,
          preferredName: preferredName.value,
          propertyName: propertyName.value,
          bookingNo: bookingNo.value,
        } satisfies DigitalKeyPersistedState,
        window.sessionStorage,
      )
    },
    { flush: 'post' },
  )

  async function fetchDigitalKeyCardImage() {
    if (digitalKeyCardImageUrl.value) return
    try {
      const url = new URL(
        'api/generic-media-resources?filters[slug][$eq]=digital-key-card&populate[0]=media',
        config.cms.apiUrl
      )
      const response = await fetch(url.toString())
      if (!response.ok) return
      const { data } = await response.json()
      digitalKeyCardImageUrl.value = data[0]?.media?.url ?? null
    } catch (error) {
      // Fail silently, gray placeholder will show if image fetching fails
    }
  }

  function reset() {
    encryptedBookingNo.value = undefined
    encryptedCheckinNo.value = undefined
    email.value = ''
    phone.value = ''
    verificationMethod.value = 'email'
    preferredName.value = ''
    propertyName.value = 'Section L'
    bookingNo.value = ''
    appleWalletJws.value = undefined
    window.sessionStorage.removeItem(DIGITAL_KEY_STORAGE_KEY)
  }

  async function requestForOtp() {
    const response = await DigitalKeysRepository.requestForOtp({
      verificationMethod: verificationMethod.value,
      email: email.value,
      encryptedBookingNo: encryptedBookingNo.value!,
    })

    return response
  }

  async function verifyOtp(otpCode: string) {
    const response = await DigitalKeysRepository.verifyOtp({
      email: email.value,
      encryptedBookingNo: encryptedBookingNo.value!,
      otpCode,
    })

    await store.dispatch('auth/setCurrentUser', response.data.guest)

    return response
  }

  function getOsName() {
    const parser = new UAParser()
    return parser.getOS().name
  }

  async function createWalletCredentials(walletType) {
    try {
      const response = await DigitalKeysRepository.requestForWalletCredentials({ email: email.value, encryptedBookingNo: encryptedBookingNo.value || '', walletType })
      const jws = response.data.payload.wallet_pass_identifiers.apple_wallet_web.jws
      return {
        state: 'pass-provisition-request-created',
        jws: {
          header: {
            kid: jws.header.kid,
          },
          payload: jws.payload,
          protected: jws.protected,
          signature: jws.signature,
        },
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        await store.dispatch('setAlert', {
          messages: [t('stores.digitalKey.errors.featureNotAvailable.message')],
          buttonText: t('stores.digitalKey.errors.featureNotAvailable.buttonText'),
          buttonAction: async () => router.push({ name: 'Home' }),
          hideX: true,
        })
        throw error
      }
      const errorCode = error.response?.data?.code
      if (typeof errorCode === 'number' && errorCode >= 100 && errorCode <= 125) {
        await store.dispatch('setAlert', {
          messages: [t('stores.digitalKey.errors.retryable.message')],
          buttonText: t('stores.digitalKey.errors.retryable.buttonText'),
          buttonAction: () => router.push({
            name: 'Digital Keys Auth',
            query: {
              encryptedBookingNo: encryptedBookingNo.value,
              encryptedCheckinNo: encryptedCheckinNo.value,
              email: email.value,
              phone: phone.value,
              firstName: preferredName.value
            }
          }),
          hideX: true,
        })
      } else {
        await store.dispatch('setAlert', {
          messages: [t('stores.digitalKey.errors.nonRetryable.message')],
          buttonText: t('stores.digitalKey.errors.nonRetryable.buttonText'),
          buttonAction: async () => {
            window.open(`mailto:${email.value}`)
            await router.push({ name: 'Home' })
          },
          hideX: true,
        })
      }
      throw error
    }
  }

  return {
    encryptedBookingNo,
    encryptedCheckinNo,
    verificationMethod,
    email,
    phone,
    preferredName,
    propertyName,
    bookingNo,
    appleWalletJws,
    digitalKeyCardImageUrl,
    fetchDigitalKeyCardImage,
    requestForOtp,
    verifyOtp,
    getOsName,
    createWalletCredentials,
    reset,
  }
})
